var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
// External dependencies
import { useContext, useState, memo, } from 'react';
import { Box, Typography, Grid, Card, CardActionArea, IconButton, MenuItem, Menu, } from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
// Internal dependencies
import { appStyles } from '../App.styles';
import { getAuthHeader, UserContext } from '../auth';
import { AdminAvatarIconSG, DeleteConfirmButtonSG } from '../components';
import { axiosClient } from '../axios';
import { ENDPOINTS, LOG_LEVELS, SERVICES } from '../constants';
/**
 * Workflows Grid
 * @param props Props
 * @returns Worflows Grid
 */
export const WorkflowsGrid = memo(function ({ workflows, setWorkflowOpen, setWorkflow, updateWorkflowsUpdatedFlag, }) {
    // Get user credential
    const user = useContext(UserContext);
    // Get translation function
    const { t, i18n } = useTranslation();
    const handleWorkflowClick = (workflow) => {
        setWorkflow(workflow);
        setWorkflowOpen(true);
    };
    const [
    /** More button anchor */ moreButtonAnchor, setMoreButtonAnchor] = useState(null);
    const /** More button open flag */ moreButtonOpen = Boolean(moreButtonAnchor);
    /**
     * Handle close More button
     */
    const handleMoreButtonClose = () => {
        setMoreButtonAnchor(null);
    };
    const [
    /** Delete prompt ids */ deleteWorkflowIds, setDeleteWorkflowIds,] = useState([]);
    /**
     * Handle More button click
     * @param event Click event
     */
    const handleMoreButtonClick = (event, workflowId) => {
        setDeleteWorkflowIds([workflowId]);
        setMoreButtonAnchor(event.currentTarget);
    };
    /** Handle Delete confirm click */
    const handleDeleteConfirmClick = () => __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c;
        try {
            // Set headers
            const /** Auth headers */ authHeader = yield getAuthHeader(user);
            const /** Request headers */ headers = Object.assign(Object.assign({}, authHeader), { 'Accept-Language': i18n.language });
            // Call Delete Files APIs
            const res = yield axiosClient.delete(`${process.env.SERVER_URL}${ENDPOINTS.WORKFLOW}${ENDPOINTS.DELETE_WORKFLOWS}`, { headers, data: { ids: deleteWorkflowIds } });
        }
        catch (err) {
            // Log error
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `Infobase, handleDeleteConfirmClick, ${err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.DOCUMENTS,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                },
            });
            if ((_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.status) {
                // If the error is from the server response
                // notify user with the server message
                toast.error((_c = (_b = err.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.message);
            }
            else {
                toast.error(t('Something went wrong. We are figuring it out.'));
            }
        }
        ;
        setDeleteWorkflowIds([]);
        updateWorkflowsUpdatedFlag();
    });
    return (_jsxs(Grid, Object.assign({ container: true, spacing: 3, sx: { padding: '1.5rem 0 0 0' } }, { children: [workflows.map((workflow, idx) => {
                return (_jsx(Grid, Object.assign({ item: true, xs: 12, sm: 6, md: 12, xxl: 6, sx: { display: 'flex', justifyContent: 'stretch' } }, { children: _jsx(Card, Object.assign({ sx: {
                            boxShadow: 'none',
                            border: '1px solid',
                            borderColor: 'greyLight.main',
                            backgroundColor: 'whiteDark.main',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'flex-start',
                            width: '100%',
                        }, onClick: () => { handleWorkflowClick(workflow); } }, { children: _jsxs(CardActionArea, Object.assign({ sx: {
                                height: '100%',
                                padding: '1rem',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-start',
                            } }, { children: [_jsxs(Grid, Object.assign({ container: true }, { children: [_jsx(Grid, Object.assign({ item: true, xs: true }, { children: _jsx(Typography, Object.assign({ variant: 'h2', sx: Object.assign(Object.assign({}, appStyles.text22), { color: 'blackDark.main', width: '100%', padding: '0 0 1.5rem', textAlign: 'left' }) }, { children: workflow === null || workflow === void 0 ? void 0 : workflow.name })) })), _jsx(Grid, { children: _jsx(IconButton, Object.assign({ onClick: (event) => {
                                                    event.stopPropagation();
                                                    handleMoreButtonClick(event, (workflow === null || workflow === void 0 ? void 0 : workflow._id) || '');
                                                }, sx: { color: 'blackDark.main' }, disabled: (workflow === null || workflow === void 0 ? void 0 : workflow._id) ? false : true }, { children: _jsx(MoreVert, {}) })) })] })), _jsx(Typography, Object.assign({ variant: 'h3', sx: Object.assign(Object.assign({}, appStyles.text16), { color: '#3F4945', width: '100%', padding: '0', margin: '0 0 1.5rem', textAlign: 'left', whiteSpace: 'pre-wrap', display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: '3', overflow: 'hidden', textOverflow: 'ellipsis' }) }, { children: workflow === null || workflow === void 0 ? void 0 : workflow.description })), _jsxs(Grid, Object.assign({ container: true, spacing: 1 }, { children: [_jsx(Grid, Object.assign({ item: true, sx: { display: 'flex', alignItems: 'center' } }, { children: _jsx(AdminAvatarIconSG, {}) })), _jsx(Grid, Object.assign({ item: true, sx: { display: 'flex', alignItems: 'center' } }, { children: _jsx(Typography, Object.assign({ sx: Object.assign(Object.assign({}, appStyles.text14), { color: '#3F4945', textAlign: 'left' }) }, { children: workflow === null || workflow === void 0 ? void 0 : workflow.author })) }))] }))] })) })) }), (workflow === null || workflow === void 0 ? void 0 : workflow._id) ? workflow._id : workflow === null || workflow === void 0 ? void 0 : workflow.name));
            }), _jsx(Menu, Object.assign({ anchorEl: moreButtonAnchor, id: "more-menu", open: moreButtonOpen, onClose: handleMoreButtonClose, transformOrigin: { horizontal: 'right', vertical: 'top' }, anchorOrigin: { horizontal: 'right', vertical: 'bottom' }, sx: {
                    '& div': {
                        borderRadius: '1rem',
                    },
                    '& ul': Object.assign({ backgroundColor: 'white.main' }, appStyles.boxShadow),
                } }, { children: _jsx(Box, { children: _jsx(MenuItem, { children: _jsx(DeleteConfirmButtonSG, { disabled: false, handleDeleteConfirmClick: handleDeleteConfirmClick, cleanOthers: handleMoreButtonClose }) }) }) }))] })));
});
